<template>
  <modal name="modal-fleet-car" class="fleet-car-modal modal-inner" transition="pop-out" :width="modalWidth" :focus-trap="true" :min-height="600" height="auto" :scrollable="true" :adaptive="true" :reset="true" :clickToClose="false">
    <span class="close-button" @click="hide()"><BaseIcon icon="times-circle" class="text-white"/></span>
    <div class="modal-header less-margin-bottom text-lg bg-primary-50 font-serif">
      {{ isEditMode ? $t('fleet_management.edit_car') : $t('fleet_management.create_car') }}
    </div>

    <div class="modal-body less-side-padding responsive-height">
      <div class="px-6 py-5 space-y-5">

      <!-- Section: Vehicle Identity -->
      <div class="space-y-3">
        <p class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ $t('fleet_management.section_identity') }}</p>

        <!-- Car Name -->
        <div>
          <label class="text-sm font-semibold text-gray-600 block mb-1">
            {{ $t('fleet_management.car_name') }} <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.car_name"
            type="text"
            :placeholder="$t('fleet_management.car_name_placeholder')"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-500"
          />
        </div>

        <!-- Car Brand + Model side by side -->
        <div class="grid grid-cols-2 gap-8">
          <div>
            <div class="flex items-center justify-between mb-1">
              <label class="text-sm font-semibold text-gray-600">{{ $t('fleet_management.car_brand') }}</label>
              <button type="button" class="text-xs text-primary-500 hover:text-primary-700 flex items-center gap-1" @click="openBrandManager">
                <BaseIcon icon="cog" style="font-size:11px" />
                {{ $t('fleet_management.manage_brands') }}
              </button>
            </div>
            <select
              v-model="form.car_brand"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-500 bg-white"
              :disabled="isBrandsLoading"
              @change="onBrandChange"
            >
              <option value="">{{ isBrandsLoading ? $t('fleet_management.loading_brands') : $t('fleet_management.select_brand') }}</option>
              <option v-for="brand in brands" :key="brand._id" :value="brand.brand_name">{{ brand.brand_name }}</option>
            </select>
          </div>
          <div>
            <label class="text-sm font-semibold text-gray-600 block mb-1">{{ $t('fleet_management.car_type') }}</label>
            <select
              v-model="form.car_model"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-500 bg-white"
              :disabled="!form.car_brand || availableModels.length === 0"
            >
              <option value="">{{ $t('fleet_management.select_model') }}</option>
              <option v-for="model in availableModels" :key="model._id" :value="model.model_name">{{ model.model_name }}</option>
            </select>
          </div>
        </div>
        <FleetBrandModal ref="brandManagerModal" @closed="loadCarBrands" />

        <!-- License Plate + Purchased Year side by side -->
        <div class="grid grid-cols-2 gap-8">
          <div>
            <label class="text-sm font-semibold text-gray-600 block mb-1">{{ $t('fleet_management.license_plate') }}</label>
            <input
              v-model="form.license_plate"
              type="text"
              :placeholder="$t('fleet_management.license_plate_placeholder')"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-500"
            />
          </div>
          <div>
            <label class="text-sm font-semibold text-gray-600 block mb-1">{{ $t('fleet_management.purchased_year') }}</label>
            <select
              v-model="form.purchased_year"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-500 bg-white"
            >
              <option :value="null">{{ $t('fleet_management.select_year') }}</option>
              <option v-for="yr in yearOptions" :key="yr" :value="yr">{{ yr }}</option>
            </select>
          </div>
        </div>

        <!-- Fuel Type + Leasing Type side by side -->
        <div class="grid grid-cols-2 gap-8">
          <div>
            <label class="text-sm font-semibold text-gray-600 block mb-1">{{ $t('fleet_management.fuel_type') }}</label>
            <select
              v-model="form.fuel_type"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-500 bg-white"
            >
              <option value="">{{ $t('fleet_management.select_fuel') }}</option>
              <option value="petrol">{{ $t('fleet_management.fuel_petrol') }}</option>
              <option value="diesel">{{ $t('fleet_management.fuel_diesel') }}</option>
              <option value="hybrid">{{ $t('fleet_management.fuel_hybrid') }}</option>
              <option value="electric">{{ $t('fleet_management.fuel_electric') }}</option>
              <option value="gas">{{ $t('fleet_management.fuel_gas') }}</option>
            </select>
          </div>
          <div>
            <label class="text-sm font-semibold text-gray-600 block mb-1">{{ $t('fleet_management.leasing_type') }}</label>
            <select
              v-model="form.leasing_type"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-500 bg-white"
            >
              <option value="">{{ $t('fleet_management.select_leasing') }}</option>
              <option value="financial">{{ $t('fleet_management.leasing_financial') }}</option>
              <option value="operational">{{ $t('fleet_management.leasing_operational') }}</option>
              <option value="cash">{{ $t('fleet_management.leasing_cash') }}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="border-t border-gray-100"></div>

      <!-- Section: Service -->
      <div class="space-y-3">
        <p class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ $t('fleet_management.section_service') }}</p>

        <!-- Mileage at registration + Service interval side by side -->
        <div class="grid grid-cols-2 gap-8">
          <div>
            <label class="text-sm font-semibold text-gray-600 block mb-1">{{ $t('fleet_management.mileage_at_registration') }}</label>
            <div class="relative">
              <input
                v-model.number="form.mileage_at_registration"
                type="number"
                min="0"
                :placeholder="$t('fleet_management.mileage_placeholder')"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-500 pr-10"
              />
              <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">km</span>
            </div>
          </div>
          <div>
            <label class="text-sm font-semibold text-gray-600 block mb-1">{{ $t('fleet_management.service_interval') }}</label>
            <div class="relative">
              <input
                v-model.number="form.service_interval_miles"
                type="number"
                min="0"
                :placeholder="$t('fleet_management.service_interval_placeholder')"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-500 pr-10"
              />
              <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">km</span>
            </div>
          </div>
        </div>

        <!-- Service notification email -->
        <div>
          <label class="text-sm font-semibold text-gray-600 block mb-1">{{ $t('fleet_management.service_notification_email') }}</label>
          <input
            v-model="form.service_notification_email"
            type="email"
            :placeholder="$t('fleet_management.service_email_placeholder')"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-500"
          />
          <p class="text-xs text-gray-400 mt-1">{{ $t('fleet_management.service_email_hint') }}</p>
        </div>
      </div>

      <div class="border-t border-gray-100"></div>

      <!-- Section: Device -->
      <div class="space-y-3">
        <p class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ $t('fleet_management.section_device') }}</p>

        <div>
          <label class="text-sm font-semibold text-gray-600 block mb-1">{{ $t('fleet_management.linked_device') }}</label>
          <treeselect
            v-model="form.imei_number"
            :options="$store.getters.getUnitOptions"
            :multiple="false"
            :disable-branch-nodes="true"
            value-consists-of="LEAF_PRIORITY"
            :noChildrenText="$t('trip_planner.no_activity')"
            :placeholder="$t('fleet_management.linked_device_placeholder')"
            @input="onDeviceSelected"
          />
          <p class="text-xs text-gray-400 mt-1">{{ $t('fleet_management.linked_device_hint') }}</p>
        </div>
      </div>

      <div class="border-t border-gray-100"></div>

      <!-- Notes -->
        <div>
          <label class="text-sm font-semibold text-gray-600 block mb-1">{{ $t('fleet_management.notes') }}</label>
          <textarea
            v-model="form.notes"
            :placeholder="$t('fleet_management.notes_placeholder')"
            rows="2"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-500 resize-none"
          />
        </div>
      </div>
    </div>

    <div class="modal-footer px-6 pt-4">
      <button class="btn-red-outline px-3 py-2 float-left" @click="hide()">
        {{ $t('user_actions.cancel') }}<BaseIcon icon="times-circle" class="ml-1" />
      </button>
      <button class="btn-blue px-3 py-2 float-right" @click="submit" :disabled="isSaving">
        <BaseIcon v-if="isSaving" icon="spinner" class="mr-1 animate-spin" />
        {{ isEditMode ? $t('fleet_management.save_changes') : $t('fleet_management.create_car') }}<BaseIcon v-if="!isSaving" icon="save" class="ml-1" />
      </button>
    </div>
  </modal>
</template>

<script>
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import FleetBrandModal from "@/components/modal/secure_route/fleet_brand_modal.vue";
const MODAL_WIDTH = 1000;

export default {
  name: "FleetCarModal",
  components: { Treeselect, FleetBrandModal },

  data() {
    return {
      modalWidth: MODAL_WIDTH,
      isEditMode: false,
      isSaving: false,
      isBrandsLoading: false,
      editId: null,
      brands: [],
      form: this.emptyForm(),
    };
  },

  computed: {
    availableModels() {
      if (!this.form.car_brand) return [];
      const found = this.brands.find(b => b.brand_name === this.form.car_brand);
      return found ? found.models : [];
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear; y >= 1980; y--) {
        years.push(y);
      }
      return years;
    },
  },

  methods: {
    emptyForm() {
      return {
        car_name: "",
        car_brand: "",
        car_model: "",
        license_plate: "",
        purchased_year: null,
        fuel_type: "",
        leasing_type: "",
        mileage_at_registration: null,
        service_interval_miles: null,
        service_notification_email: "",
        imei_number: null,
        unit_id: "",
        notes: "",
      };
    },

    openCreate() {
      this.isEditMode = false;
      this.editId = null;
      this.form = this.emptyForm();
      this.loadCarBrands();
      this.$modal.show("modal-fleet-car");
    },

    openEdit(vehicle) {
      this.isEditMode = true;
      this.editId = vehicle._id;
      this.form = {
        car_name: vehicle.car_name || "",
        car_brand: vehicle.car_brand || "",
        car_model: vehicle.car_model || "",
        license_plate: vehicle.license_plate || "",
        purchased_year: vehicle.purchased_year || null,
        fuel_type: vehicle.fuel_type || "",
        leasing_type: vehicle.leasing_type || "",
        mileage_at_registration: vehicle.mileage_at_registration || null,
        service_interval_miles: vehicle.service_interval_miles || null,
        service_notification_email: vehicle.service_notification_email || "",
        imei_number: vehicle.imei_number || null,
        unit_id: vehicle.unit_id || "",
        notes: vehicle.notes || "",
      };
      this.loadCarBrands();
      this.$modal.show("modal-fleet-car");
    },

    hide() {
      this.$modal.hide("modal-fleet-car");
    },

    async loadCarBrands() {
      this.isBrandsLoading = true;
      try {
        const res = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/fleet-car-brands`);
        this.brands = res.data.brands || [];
      } catch (err) {
        // silent — brands optional
      } finally {
        this.isBrandsLoading = false;
      }
    },

    openBrandManager() {
      this.$refs.brandManagerModal.open();
    },

    onBrandChange() {
      this.form.car_model = "";
    },

    onDeviceSelected(imei) {
      const options = this.$store.getters.getUnitOptions || [];
      let unit_id = "";
      const findUnit = (nodes) => {
        for (const node of nodes) {
          if (node.id === imei) {
            unit_id = node.unit_id || "";
            return;
          }
          if (node.children) findUnit(node.children);
        }
      };
      findUnit(options);
      this.form.unit_id = unit_id;
    },

    async submit() {
      if (!this.form.car_name || !this.form.car_name.trim()) {
        this.handleError(null, this.$t('fleet_management.car_name_required'));
        return;
      }

      this.isSaving = true;
      try {
        const customerId = this.$route.params.id;
        const payload = {
          car_name: this.form.car_name.trim(),
          car_brand: this.form.car_brand || "",
          car_model: this.form.car_model || "",
          license_plate: (this.form.license_plate || "").trim(),
          purchased_year: this.form.purchased_year || null,
          fuel_type: this.form.fuel_type || "",
          leasing_type: this.form.leasing_type || "",
          mileage_at_registration: this.form.mileage_at_registration || null,
          service_interval_miles: this.form.service_interval_miles || null,
          service_notification_email: (this.form.service_notification_email || "").trim(),
          imei_number: this.form.imei_number || "",
          unit_id: this.form.unit_id || "",
          notes: (this.form.notes || "").trim(),
          customer_id: customerId,
        };

        if (this.isEditMode) {
          await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/fleet-vehicles/${this.editId}`, payload);
          this.handleSuccess(this.$t('fleet_management.car_updated'));
        } else {
          await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/fleet-vehicles`, payload);
          this.handleSuccess(this.$t('fleet_management.car_created'));
        }

        this.$emit("saved");
        this.hide();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isSaving = false;
      }
    },
  },
  created() {
    this.modalWidth = window.innerWidth < MODAL_WIDTH ? window.innerWidth : MODAL_WIDTH;
  },
};
</script>
<style scoped>
.fleet-car-modal {
  display: flex;
  flex-direction: column;
}

.fleet-car-modal .modal-body {
  height: calc(100vh - 100px);
  overflow-y: scroll;
}
.fleet-car-modal .modal-header {
 margin-bottom: 0px !important;
}
.fleet-car-modal .modal-footer {
 position: relative !important;
 height: 65px;
}
</style>